/**
 * Run a reset when a value CHANGES — never on the first render.
 *
 * "A cohort id cannot outlive a change of client" is a rule three surfaces
 * need (the roster filter, the single create form, the batch form), and it
 * had three different implementations. Two of them were effects keyed on
 * the client id, which fire on mount as well as on change — and mount is
 * exactly when a deep link has just supplied the value the reset destroys.
 * `batch-create-page.tsx` carried a `previousClientId` ref to suppress that
 * run; `create-student-page.tsx` did not, and was correct only because it
 * happened not to seed `cohortId` from the query string. Adding that seed —
 * the obvious symmetry between the two pages — would have broken the link
 * on first paint, silently.
 *
 * So the guard lives here instead of being remembered per call site.
 *
 * Not for filter state that lives in the URL: the roster clears `cohortId`
 * inside the picker's `onChange`, alongside the `setSearchParams` call that
 * owns the same transition. Splitting that across an effect would put two
 * writers on one query string.
 */
import { useEffect, useRef } from 'react'

export function useResetOnChange<T>(value: T, reset: () => void): void {
  const previous = useRef(value)
  // `reset` is typically an inline closure, so it is deliberately NOT a
  // dependency — including it would re-run this on every render of the
  // parent, which is the mount-run problem again wearing a different hat.
  const latest = useRef(reset)
  latest.current = reset

  useEffect(() => {
    if (Object.is(previous.current, value)) return
    previous.current = value
    latest.current()
  }, [value])
}
